import React, { useEffect, useState } from 'react';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { notification } from '@spark/ui-components/utils/notification';
import { downloadCandidatoArquivo, alterarFavorito } from '@spark/services/recrutamento-rh';
import { generateCurriculoPdf } from './CandidatoCurriculoPdf';

const buildWhatsAppHref = (numeroTelefone, mensagem = '') => {
  if (!numeroTelefone) return '';
  const digits = String(numeroTelefone).replace(/\D/g, '');
  const withCC = digits.startsWith('55') ? digits : `55${digits}`;
  const text = encodeURIComponent(mensagem);
  return text ? `https://wa.me/${withCC}?text=${text}` : `https://wa.me/${withCC}`;
};

const HeartIcon = ({ filled, popping }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    style={{
      transition: 'transform 150ms ease, fill 150ms ease, stroke 150ms ease',
      transform: popping ? 'scale(1.25)' : 'scale(1)',
      display: 'block'
    }}
    fill={filled ? '#e0245e' : 'none'}
    stroke={filled ? '#e0245e' : '#666'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

const CandidatoQuickActions = ({ candidato = {} }) => {
  const [downloading, setDownloading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [isFav, setIsFav] = useState(!!candidato?.Favorito);
  const [favLoading, setFavLoading] = useState(false);
  const [pop, setPop] = useState(false);

  useEffect(() => {
    setIsFav(!!candidato?.Favorito);
  }, [candidato?.id]);

  const phoneHref = buildWhatsAppHref(candidato?.NumeroTelefone, 'Olá!');

  const safeDownload = async (tipo, label) => {
    if (!candidato?.id) return;
    setDownloading(true);
    try {
      await downloadCandidatoArquivo(candidato.id, tipo);
      notification.success(`Documento baixado com sucesso.`);
    } catch (err) {
      const status =
        err?.response?.status ??
        err?.status ??
        Number(/\b(\d{3})\b/.exec(String(err?.message || ''))?.[1]);
      if (status === 404) {
        notification.warning(`${label} não encontrado(a) para este candidato.`);
      } else {
        notification.error?.(`Falha ao baixar ${label.toLowerCase()}. Tente novamente.`)
          ?? notification.warning(`Falha ao baixar ${label.toLowerCase()}. Tente novamente.`);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCurriculo = () => safeDownload('curriculo', 'Currículo');
  const handleDownloadCnh = () => safeDownload('cnh', 'CNH');

  const handleGenerateCurriculo = async () => {
    if (!candidato?.id) return;
    setGeneratingPdf(true);
    try {
      generateCurriculoPdf(candidato);
      notification.success('Curriculo gerado com sucesso.');
    } catch (err) {
      notification.error?.('Falha ao gerar o curriculo. Tente novamente.')
        ?? notification.warning('Falha ao gerar o curriculo. Tente novamente.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleToggleFavorito = async () => {
    if (!candidato?.id) return;
    const proximo = !isFav;

    setIsFav(proximo);
    setFavLoading(true);

    try {
      await alterarFavorito(candidato.id, proximo);
      notification.success(proximo ? 'Adicionado aos favoritos.' : 'Removido dos favoritos.');
      setPop(true);
      setTimeout(() => setPop(false), 160);
    } catch (err) {
      setIsFav(!proximo);
      notification.error?.('Não foi possível alterar o favorito. Tente novamente.')
        ?? notification.warning('Não foi possível alterar o favorito. Tente novamente.');
    } finally {
      setFavLoading(false);
    }
  };

  return (
    <div
      className="flex gap-2 flex-wrap"
      style={{
        marginBottom: '1rem',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.5rem',
        padding: '0.75rem',
        borderRadius: '0.25rem',
      }}
    >
      <Button
        variant="success"
        as="a"
        href={phoneHref || '#'}
        target="_blank"
        rel="noopener noreferrer"
        disabled={!phoneHref}
        onClick={(e) => {
          if (!phoneHref) e.preventDefault();
        }}
      >
        WhatsApp
      </Button>

      <ButtonWithLoading
        variant="secondary"
        onClick={handleDownloadCurriculo}
        loading={downloading}
        disabled={downloading || !candidato?.id}
      >
        Baixar currículo (Anexo)
      </ButtonWithLoading>

      <ButtonWithLoading
        variant="secondary"
        onClick={handleGenerateCurriculo}
        loading={generatingPdf}
        disabled={generatingPdf || !candidato?.id}
      >
        Gerar curriculo (PDF)
      </ButtonWithLoading>


      <ButtonWithLoading
        variant="secondary"
        onClick={handleDownloadCnh}
        loading={downloading}
        disabled={downloading || !candidato?.id}
      >
        Baixar CNH
      </ButtonWithLoading>

      {/* ⭐ Botão Favorito */}
      <ButtonWithLoading
        variant={isFav ? 'warning' : 'secondary'}
        onClick={handleToggleFavorito}
        loading={favLoading}
        disabled={favLoading || !candidato?.id}
        aria-pressed={isFav}
        title={isFav ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontWeight: 600
        }}
      >
        <HeartIcon filled={isFav} popping={pop} />
        {isFav ? 'Favorito' : 'Favoritar'}
      </ButtonWithLoading>
    </div>
  );
};

export default CandidatoQuickActions;
